import { useMutation, useQueryClient } from '@tanstack/react-query';
import { companyApi } from 'shared/services/companyApi';
import { CompanyThemeSettings } from 'shared/types';
import { queryKeys } from '../queryConfig';

/**
 * Hook to update company theme settings
 *
 * @returns Mutation for updating company theme
 */
export const useUpdateCompanyTheme = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      theme,
    }: {
      companyId: string;
      theme: Partial<CompanyThemeSettings>;
    }) => {
      const response = await companyApi.updateTheme(companyId, theme);

      if (!response.success) {
        throw new Error(response.error || 'Failed to update company theme');
      }

      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate theme queries for this company
      queryClient.invalidateQueries({
        queryKey: queryKeys.companyTheme.theme(variables.companyId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.companyTheme.themePublic(variables.companyId),
      });
      // A theme save can also change logoUrl (it's part of the same
      // settings.theme document) — invalidate the logo query too so
      // Sidebar/CompanyDetail branding doesn't serve a stale value.
      queryClient.invalidateQueries({
        queryKey: queryKeys.companyTheme.logoUrl(variables.companyId),
      });
    },
  });
};

/**
 * Hook to reset company theme to defaults
 *
 * @returns Mutation for resetting company theme
 */
export const useResetCompanyTheme = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (companyId: string) => {
      const response = await companyApi.resetTheme(companyId);

      if (!response.success) {
        throw new Error(response.error || 'Failed to reset company theme');
      }

      return response;
    },
    onSuccess: (_, companyId) => {
      // Invalidate theme queries for this company
      queryClient.invalidateQueries({
        queryKey: queryKeys.companyTheme.theme(companyId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.companyTheme.themePublic(companyId),
      });
    },
  });
};
